import { isEqual } from "lodash";
import type { FC, Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import { arraifyFilters } from "../../../assets/Services/wsSearch";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import {
  datetimeRangeToString,
  SMALL_SCREEN,
} from "../../../assets/Utils/generic";
import { useSearch, parseParams } from "../../hooks/useSearch";
import { cn } from "../../../assets/Utils/generic";
import { useSearchParams } from "react-router-dom";
import { ANALYTICS_SOURCE } from "../../../config/analytics";
import DatesInput from "./DatesInput";
import GuestsInput from "./GuestsInput";
import DestinationInput from "./DestinationInput";

export const ScrollLock = () => {
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });

  useEffect(() => {
    if (isSmallScreen) {
      document.querySelector("html")!.classList.add("scroll-lock");
      document.querySelector("body")!.classList.add("scroll-lock");
    }

    if (!isSmallScreen) {
      document.querySelector("html")!.classList.remove("scroll-lock");
      document.querySelector("body")!.classList.remove("scroll-lock");
    }

    return () => {
      if (isSmallScreen) {
        document.querySelector("html")!.classList.remove("scroll-lock");
        document.querySelector("body")!.classList.remove("scroll-lock");
      }
    };
  }, [isSmallScreen]);

  return <></>;
};

const MobileMinified: FC<{
  showFilters: boolean;
  setIsMobileMinified?: Dispatch<SetStateAction<boolean>>;
}> = ({ showFilters, setIsMobileMinified }) => {
  const { t } = useTranslation();
  const { searchParameters } = useSearch();
  return (
    <div id="searchbar" className="d-flex flex-column gap-2">
      <div
        className="d-flex gap-2"
        onClick={() => {
          fireTagManagerEvent("open_search");
          setIsMobileMinified?.((v) => !v);
        }}
      >
        <div
          style={{
            borderRadius: "8px",
            boxShadow: "4px 3px 10px 1px rgba(0, 0, 0, 0.05)",
            userSelect: "none",
            border: "1px solid lightgray",
            // maxWidth: "calc(100% - 48px)",
          }}
          className="w100 d-flex align-items-center gap-2 p-3 py-2 cursor-pointer white-background"
        >
          <IconNew icon="search" />
          <span className="fs-body-sm medium text-truncate">
            {searchParameters?.destinationLabel?.split(",")[0] ?? ""}
            {", "}
            {datetimeRangeToString(
              searchParameters?.arrivalDate,
              searchParameters?.departureDate,
              -new Date().getTimezoneOffset(),
              false,
            )}
            , {searchParameters?.adults ?? 2} {t("ds.guests")}
          </span>
        </div>

        {/* showFilters && (
					<div className="position-relative">
						<Button
							size="sm"
							variant="light"
							icon="filterList"
							onClick={(e) => {
								e.stopPropagation();
								openModal({
									content: (
										<FiltersModal
											onFilter={({filters, sortBy}) => {
												goToSearchUrl(
													{ filters, sortBy },
													{
														triggerTagManagerEvent: true,
													},
												);
												closeModal();
											}}
										/>
									),
									className: "rounded-t-3xl self-end h-[calc(100dvh-1rem)] lg:h-auto lg:self-center lg:rounded-3xl"
								}, true);
							}}
						/>
						<FilterBadge />
					</div>
				) */}
      </div>
      {/* <FilterTags /> */}
    </div>
  );
};

const MobileVersion: FC<{
  isSmallScreen: boolean;
  setIsMobileMinified?: Dispatch<SetStateAction<boolean>>;
  parent: "home" | "nav" | "detail";
}> = ({ isSmallScreen, setIsMobileMinified, parent }) => {
  const { t } = useTranslation();
  const { goToSearchUrl, searchParameters, setParameters } = useSearch();

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);

  const locationInputRef = useRef<HTMLDivElement>(null);
  const calendarInputRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        id="mobile-searchbar"
        style={{
          ...(window.location.pathname.includes("/directsearch")
            ? {
                position: "fixed",
                width: "calc(100% - 40px)",
                borderRadius: "24px",
                padding: "8px 0 10px 0",
              }
            : { borderRadius: "24px", padding: "8px 0 10px 0" }),
        }}
        className="w-full flex flex-col bg-white"
      >
        <div className="d-flex flex-column gap-1">
          <DestinationInput
            mode="mobile"
            setIsCalendarOpen={setIsCalendarOpen}
            setIsGuestsOpen={setIsGuestsOpen}
            setIsLocationOpen={setIsLocationOpen}
            isLocationOpen={isLocationOpen}
            locationInputRef={locationInputRef}
            parent={parent}
          />
          <div className="border-bottom"></div>
          <DatesInput
            mode="mobile"
            isSmallScreen={isSmallScreen}
            isCalendarOpen={isCalendarOpen}
            setIsCalendarOpen={setIsCalendarOpen}
            setIsLocationOpen={setIsLocationOpen}
            setIsGuestsOpen={setIsGuestsOpen}
            setParameters={(sp) => setParameters(sp)}
            params={searchParameters}
            calendarInputRef={calendarInputRef}
            parent={parent}
          />
          <div className="border-bottom"></div>
          <GuestsInput
            mode="mobile"
            isGuestsOpen={isGuestsOpen}
            setIsGuestsOpen={setIsGuestsOpen}
            setIsLocationOpen={setIsLocationOpen}
            setIsCalendarOpen={setIsCalendarOpen}
            calendarInputRef={calendarInputRef}
            locationInputRef={locationInputRef}
            parent={parent}
          />
        </div>
        <div className="flex flex-col gap-2" style={{ padding: "0 16px" }}>
          <Button
            className="rounded-2xl w-full"
            style={{ padding: "14px 20px" }}
            onClick={() => {
              goToSearchUrl(
                {},
                {
                  triggerTagManagerEvent:
                    window.location.pathname.includes("/directsearch"),
                  source: ANALYTICS_SOURCE.LANDING,
                },
              );
              setIsMobileMinified?.(true);
            }}
            text={
              window.location.pathname.includes("/directsearch")
                ? t("ds.search")
                : t("ds.letsgo")
            }
            disabled={
              (!searchParameters.destinationLabel && !searchParameters.tag) ||
              !searchParameters.arrivalDate ||
              !searchParameters.departureDate ||
              (!searchParameters.adults && !searchParameters.childrenAge)
            }
          />
          {!window.location.pathname.includes("/directsearch") && (
            <p
              className="fs-body-xs text-center text-black lg:hidden"
              dangerouslySetInnerHTML={{ __html: t("ds.promo") }}
            ></p>
          )}
        </div>
      </div>
    </>
  );
};

export const FilterBadge: FC = () => {
  const {
    currentSearchParameters: { filters },
  } = useSearch();

  const arrayfiedFilters = useMemo(() => arraifyFilters(filters), [filters]);

  return arrayfiedFilters.length > 0 ? (
    <div
      className="rounded-5 bg-black text-white fs-body-xs d-flex align-items-center justify-content-center position-absolute"
      style={{
        width: "20px",
        height: "20px",
        right: "-8px",
        top: "0px",
        lineHeight: "20px",
        userSelect: "none",
      }}
    >
      {arrayfiedFilters.length}
    </div>
  ) : null;
};

const DesktopVersion: FC<{
  isSmallScreen: boolean;
  parent: "home" | "nav" | "detail";
}> = ({ isSmallScreen, parent }) => {
  const { t } = useTranslation();
  const { goToSearchUrl, searchParameters, setParameters } = useSearch();
  const [urlParams] = useSearchParams();

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);

  const locationInputRef = useRef<HTMLDivElement>(null);
  const calendarInputRef = useRef<HTMLDivElement>(null);

  const isNav = parent === "nav";

  const searchParametersState = useMemo(
    () => ({
      destinationLabel: searchParameters.destinationLabel,
      tag: searchParameters.tag,
      arrivalDate: searchParameters.arrivalDate,
      departureDate: searchParameters.departureDate,
      adults: searchParameters.adults,
      childrenAge: searchParameters.childrenAge,
    }),
    [searchParameters],
  );

  const searchParametersFromUrl = useMemo(
    () => ({
      destinationLabel:
        parseParams(
          "destinationLabel",
          urlParams.get("destinationLabel") as string,
        ) ?? undefined,
      tag: parseParams("tag", urlParams.get("tag") as string) ?? undefined,
      arrivalDate:
        parseParams("arrivalDate", urlParams.get("arrivalDate") as string) ??
        undefined,
      departureDate:
        parseParams(
          "departureDate",
          urlParams.get("departureDate") as string,
        ) ?? undefined,
      adults:
        parseParams("adults", urlParams.get("adults") as string) ?? undefined,
      childrenAge:
        parseParams("childrenAge", urlParams.get("childrenAge") as string) ??
        undefined,
    }),
    [searchParameters],
  );

  const isActionDisabled =
    (!searchParameters.destinationLabel && !searchParameters.tag) ||
    !searchParameters.arrivalDate ||
    !searchParameters.departureDate ||
    (!searchParameters.adults && !searchParameters.childrenAge);

  return (
    <div
      className={`flex flex-col ${isNav ? "py-[6px]" : ""}`}
      style={{ zIndex: "1" }}
    >
      <div className="d-flex align-items-center justify-content-center w100 gap-2">
        <div
          style={{
            ...(!isNav
              ? { boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.1)" }
              : {}),
            width: !isNav ? "auto" : "100%",
          }}
          className={cn(
            "white-background flex items-center rounded-3xl px-4 py-2 h-16 lg:px-2",
            isNav && "lg:bg-[#F3F3F3] lg:py-0 lg:rounded-2xl lg:h-12 lg:pr-1.5",
          )}
        >
          <div
            className="items-center gap-2 flex h-full"
            style={{ width: !isNav ? "800px" : "100%" }}
          >
            {/* location */}
            <DestinationInput
              mode="desktop"
              setIsCalendarOpen={setIsCalendarOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              setIsLocationOpen={setIsLocationOpen}
              isLocationOpen={isLocationOpen}
              locationInputRef={locationInputRef}
              parent={parent}
            />
            <div className={cn("h-full", isNav && "py-2")}>
              <div className="bg-[#E8E8E8] w-px h-full" />
            </div>
            {/* dates */}
            <DatesInput
              mode="desktop"
              isSmallScreen={isSmallScreen}
              isCalendarOpen={isCalendarOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              setIsLocationOpen={setIsLocationOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              calendarInputRef={calendarInputRef}
              setParameters={(sp) => setParameters(sp)}
              params={searchParameters}
              parent={parent}
            />
            <div className={cn("h-full", isNav && "py-2")}>
              <div className="bg-[#E8E8E8] w-px h-full" />
            </div>
            {/* guests */}
            <GuestsInput
              mode="desktop"
              isGuestsOpen={isGuestsOpen}
              setIsGuestsOpen={setIsGuestsOpen}
              setIsLocationOpen={setIsLocationOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              calendarInputRef={calendarInputRef}
              locationInputRef={locationInputRef}
              parent={parent}
            />
          </div>
          <Button
            className={cn("ml-2", isNav && "lg:h-9")}
            textClass={cn("fs-body medium", isNav && "text-sm")}
            onClick={() => {
              goToSearchUrl(
                {},
                {
                  reset: true,
                  triggerTagManagerEvent:
                    window.location.pathname.includes("/directsearch"),
                  source: ANALYTICS_SOURCE.LANDING,
                },
              );
            }}
            text={
              window.location.pathname.includes("/directsearch")
                ? t("ds.search")
                : t("ds.letsgo")
            }
            style={{ padding: !isNav ? "14px 20px" : "14px 12px", borderRadius: !isNav ? "16px" : "12px" }}
            disabled={isActionDisabled}
          />
        </div>
        {/*showFilters && (
					<div className="position-relative">
						<Button
							textClass="fs-body-sm medium"
							// text={t("ds.filters")}
							variant="light"
							icon="filterList"
							onClick={openFiltersModal}
							style={{ borderRadius: "24px" }}
						/>
						<FilterBadge />
					</div>
				) */}
      </div>
      {/* {!isHome ? <FilterTags /> : null} */}
    </div>
  );
};

export default function Search({
  showFilters,
  mode = "desktop",
  isMobileMinified,
  setIsMobileMinified,
  isNav = false,
  parent,
}: {
  showFilters?: boolean;
  mode?: "mobile" | "desktop";
  isMobileMinified?: boolean;
  setIsMobileMinified?: Dispatch<SetStateAction<boolean>>;
  isNav?: boolean;
  parent: "home" | "nav" | "detail";
}) {
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });

  if (mode === "mobile") {
    if (isMobileMinified) {
      return (
        <MobileMinified
          showFilters={!!showFilters}
          setIsMobileMinified={setIsMobileMinified}
        />
      );
    }
    return (
      <>
        {isNav && (
          <div
            className="w-full fixed bg-black opacity-50 left-0 z-20 top-[-18px]"
            style={{ height: "calc(100dvh + 18px)" }}
            onClick={() => setIsMobileMinified?.(true)}
          ></div>
        )}
        <MobileVersion
          setIsMobileMinified={setIsMobileMinified}
          isSmallScreen={isSmallScreen}
          parent={parent}
        />
      </>
    );
  }

  return <DesktopVersion isSmallScreen={isSmallScreen} parent={parent} />;
}
